import axios from 'axios';
import type { ScaleBarcodeProfile } from '@/utils/scaleBarcode';
import { useAuthStore } from '@/store/authStore';
import { useDesktopSessionStore } from '@/desktop/desktopSessionStore';
import { isDesktopApp } from '@/desktop/electronApi';
import { hardNavigate } from '@/desktop/navigation';

const API_BASE = import.meta.env.VITE_API_URL || '/api';
const api = axios.create({
  baseURL: API_BASE,
  headers: { 'Content-Type': 'application/json' },
  withCredentials: true,
  // Without a timeout, flaky/offline networks hang POS mutations forever (Complete sale spinner).
  timeout: 20000,
});

/** Runtime override for Electron (full https://…/api URL). */
export function setApiBaseUrl(url: string) {
  if (!url) return;
  api.defaults.baseURL = url.replace(/\/$/, '');
}

api.interceptors.request.use((config) => {
  const companyCode = localStorage.getItem('company_code');
  if (companyCode) config.headers['X-Company-Code'] = companyCode;
  return config;
});

api.interceptors.response.use((r) => r, (err) => {
  const url = err.config?.url || '';
  const isAuthCheck = url.includes('get_current_user');
  const isNetwork =
    !err.response &&
    (err.message === 'Network Error' || err.code === 'ERR_NETWORK' || err.code === 'ECONNABORTED');
  const status = err.response?.status;
  const excType = String(err.response?.data?.exc_type || '');
  const serverMsg = String(err.response?.data?.message || '');
  // 403 is often a business/permission error (missing doc, no read rights) — not a dead session.
  // Only force logout on 401 or clear AuthenticationError so POS order-complete never kicks users out.
  const notLoggedIn =
    /Not logged in|Login required|Please login/i.test(serverMsg) ||
    /AuthenticationError|SessionExpired|LoginRequired/i.test(excType);
  const isAuthFailure =
    status === 401 ||
    (status === 403 && notLoggedIn);
    if (!isNetwork && !isAuthCheck && isAuthFailure) {
    const desktop = isDesktopApp();
    const localOk = desktop && useDesktopSessionStore.getState().locallyUnlocked;
    if (localOk) {
      // Soft fail: keep local unlock so offline sales continue.
      useDesktopSessionStore.getState().setCloudSessionExpired(true);
    } else {
      useAuthStore.getState().clearAuth();
      hardNavigate(desktop ? '/unlock' : '/login');
    }
  }
  return Promise.reject(err);
});

export const authApi = {
  login: async (payload: {
    username: string;
    password: string;
    company_code?: string;
    subdomain?: string;
  }) => {
    const response = await api.post('/method/saas_pos.api.auth.login', payload);
    return response;
  },
  logout: () => api.get('/method/saas_pos.api.auth.logout'),
  getCurrentUser: () => api.get('/method/saas_pos.api.auth.get_current_user'),
  getCompanyProfile: () => api.get('/method/saas_pos.api.auth.get_company_profile'),
  updateCompanyProfile: (data: unknown) => api.post('/method/saas_pos.api.auth.update_company_profile', { data }),
  updateMyPassword: (data: { current_password: string; new_password: string }) =>
    api.post('/method/saas_pos.api.auth.update_my_password', { data }),
  getMyProfile: () => api.get('/method/saas_pos.api.auth.get_my_profile'),
  updateMyProfile: (data: {
    ui_language?: string;
    current_password?: string;
    new_password?: string;
  }) => api.post('/method/saas_pos.api.auth.update_my_profile', { data }),
  checkSubdomain: (subdomain: string) =>
    api.get('/method/saas_pos.api.auth.check_subdomain', { params: { subdomain } }),
  forgotPassword: (payload: {
    username: string;
    frontend_url: string;
    company_code?: string;
    subdomain?: string;
  }) => api.post('/method/saas_pos.api.auth.forgot_password', payload),
  resetPasswordWithKey: (key: string, new_password: string) =>
    api.post('/method/saas_pos.api.auth.reset_password_with_key', { key, new_password }),
};

export const privacyApi = {
  exportCustomerData: (format: 'json' | 'csv' = 'json') =>
    api.get('/method/saas_pos.api.privacy.export_customer_data', { params: { format } }),
  anonymizeCustomer: (customer: string) =>
    api.post('/method/saas_pos.api.privacy.anonymize_customer', { customer }),
  getPrivacyNotice: () => api.get('/method/saas_pos.api.privacy.get_privacy_notice'),
};

export const saasApi = {
  signup: (data: unknown) => api.post('/method/saas_pos.api.saas.signup', { data }),
  getTrialStatus: () => api.get('/method/saas_pos.api.saas.get_trial_status'),
  loadSampleData: () => api.post('/method/saas_pos.api.saas.load_sample_data', {}),
  deleteSampleData: () => api.post('/method/saas_pos.api.saas.delete_sample_data', {}),
  factoryReset: () => api.post('/method/saas_pos.api.saas.factory_reset', {}),
  uploadFile: (formData: FormData) => api.post('/method/upload_file', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  submitPayment: (data: { package: string; billing_cycle: string; paid_amount: number; payment_method: string; transaction_reference?: string; remarks?: string; payment_proof?: string }) =>
    api.post('/method/saas_pos.api.saas.submit_payment', { data }),
  getPaymentStatus: () => api.get('/method/saas_pos.api.saas.get_payment_status'),
  getPendingSubscriptionInvoices: () => api.get('/method/saas_pos.api.saas.get_pending_subscription_invoices'),
  getSubscriptionHistory: () => api.get('/method/saas_pos.api.saas.get_subscription_history'),
  submitSubscriptionPaymentProof: (data: {
    invoice_names?: string[];
    invoice_name?: string;
    amount?: string | number;
    package?: string;
    payment_proof: string;
    payment_date: string;
    transaction_reference: string;
    remarks?: string;
    addon_codes?: string[];
    selected_add_ons?: string[];
  }) => api.post('/method/saas_pos.api.saas.submit_subscription_payment_proof', { data }),
  getPackages: () => api.get('/method/saas_pos.api.saas.get_packages'),
  createSupportTicket: (data: {
    subject: string;
    message: string;
    issue_type: string;
    priority: string;
    attachment_url?: string;
    contact_person?: string;
  }) => api.post('/method/saas_pos.api.crm.create_support_ticket', { data }),
  createSupportLead: (data: {
    subject: string;
    message: string;
    name?: string;
    email?: string;
    mobile?: string;
  }) => api.post('/method/saas_pos.api.crm.create_support_lead', { data }),
};

export const websiteApi = {
  getSettings: () => api.get('/method/saas_pos.api.website.get_settings'),
};

export const usersApi = {
  list: () => api.get('/method/saas_pos.api.users.list_company_users'),
  getManagers: () => api.get('/method/saas_pos.api.users.get_company_managers'),
  create: (data: {
    full_name: string;
    email: string;
    password: string;
    role: string;
    username?: string;
    mobile?: string;
    daily_expense_limit?: number;
    shift_expense_limit?: number;
    max_discount_pct?: number;
    can_override_min_price?: boolean;
    can_process_returns?: boolean;
    can_add_expenses?: boolean;
    can_update_item_prices?: boolean;
    can_create_items?: boolean;
    max_refund_amount?: number;
    max_refund_count?: number;
    salary?: number;
  }) => api.post('/method/saas_pos.api.users.create_company_user', { data }),
  update: (data: {
    name: string;
    role?: string;
    full_name?: string;
    mobile?: string;
    daily_expense_limit?: number;
    shift_expense_limit?: number;
    max_discount_pct?: number;
    can_override_min_price?: boolean;
    can_process_returns?: boolean;
    can_add_expenses?: boolean;
    can_update_item_prices?: boolean;
    can_create_items?: boolean;
    max_refund_amount?: number;
    max_refund_count?: number;
    salary?: number;
  }) => api.post('/method/saas_pos.api.users.update_company_user', { data }),
  setActive: (data: { name: string; is_active: boolean }) =>
    api.post('/method/saas_pos.api.users.set_company_user_active', { data }),
  resetPassword: (data: { name: string; new_password: string }) =>
    api.post('/method/saas_pos.api.users.reset_company_user_password', { data }),
  getLimits: () => api.get('/method/saas_pos.api.users.get_user_limits'),
  checkUsernameAvailability: (data: unknown) => api.post('/method/saas_pos.api.users.check_username_availability', { data }),
  verifyManagerOverride: (data: { username: string; password: string }) =>
    api.post('/method/saas_pos.api.users.verify_manager_override', { data }),
};

export const cashMovementApi = {
  create: (data: {
    movement_type: 'Paid In' | 'Paid Out' | 'Drop' | 'Transfer';
    amount: number;
    reason?: string;
    remarks?: string;
    pos_shift?: string;
    cash_pocket?: string;
  }) => api.post('/method/saas_pos.api.cash_movement.create_cash_movement', { data }),
  listForShift: (shift: string) =>
    api.get('/method/saas_pos.api.cash_movement.list_shift_movements', { params: { shift } }),
};

export const auditApi = {
  list: (params?: { action_type?: string; user?: string; from_date?: string; to_date?: string; limit?: number }) =>
    api.get('/method/saas_pos.api.audit.get_audit_logs', { params }),
  getDeletedInvoices: (params: { from_date: string; to_date: string }) =>
    api.get('/method/saas_pos.api.audit.get_deleted_invoices_report', { params }),
};

export const starterCatalogApi = {
  getCategories: () =>
    api.get('/method/saas_pos.api.starter_catalog.get_starter_catalog_categories'),
  getItems: (category: string) =>
    api.get('/method/saas_pos.api.starter_catalog.get_starter_catalog_items', { params: { category } }),
  getStoreCategories: () =>
    api.get('/method/saas_pos.api.starter_catalog.get_store_categories'),
  importItems: (data: { items: unknown[]; manage_stock: boolean; with_images: boolean }) =>
    api.post('/method/saas_pos.api.starter_catalog.import_starter_catalog_items', {
      items: JSON.stringify(data.items),
      manage_stock: data.manage_stock ? 1 : 0,
      with_images: data.with_images ? 1 : 0,
    }),
};

export const loyaltyApi = {
  getSettings: () => api.get('/method/saas_pos.api.loyalty.get_loyalty_settings'),
  saveProgram: (data: Record<string, unknown>) =>
    api.post('/method/saas_pos.api.loyalty.save_loyalty_program', { data }),
  getCustomerBalance: (customer: string) =>
    api.get('/method/saas_pos.api.loyalty.get_customer_loyalty_balance', { params: { customer } }),
};

export const terminalApi = {
  getStatus: (data: { company_code: string; terminal_ref: string }) =>
    api.post('/method/saas_pos.api.terminal_webhook.get_terminal_payment_status', { data }),
};

export const cardTerminalApi = {
  getConfig: () => api.get('/method/saas_pos.api.card_terminal.get_card_terminal_config'),
  setProvider: (provider: '' | 'Teya' | 'Dojo') =>
    api.post('/method/saas_pos.api.card_terminal.set_card_terminal_provider', { provider }),
  createPayment: (data: { amount: number; currency?: string; merchant_reference?: string }) =>
    api.post('/method/saas_pos.api.card_terminal.create_terminal_payment', { data }),
  getPaymentStatus: (data: { provider?: string; terminal_ref: string }) =>
    api.post('/method/saas_pos.api.card_terminal.get_terminal_payment_status', { data }),
  cancelPayment: (data: { provider?: string; terminal_ref: string }) =>
    api.post('/method/saas_pos.api.card_terminal.cancel_terminal_payment', { data }),
  getTeyaSettings: () => api.get('/method/saas_pos.api.teya.settings.get_tenant_teya_settings'),
  saveTeyaSettings: (data: Record<string, unknown>) =>
    api.post('/method/saas_pos.api.teya.settings.save_tenant_teya_settings', { data }),
  testTeya: (data?: Record<string, unknown>) =>
    api.post('/method/saas_pos.api.teya.settings.test_tenant_teya_connection', { data: data || {} }),
  getDojoSettings: () => api.get('/method/saas_pos.api.dojo.settings.get_tenant_dojo_settings'),
  saveDojoSettings: (data: Record<string, unknown>) =>
    api.post('/method/saas_pos.api.dojo.settings.save_tenant_dojo_settings', { data }),
  testDojo: (data?: Record<string, unknown>) =>
    api.post('/method/saas_pos.api.dojo.settings.test_tenant_dojo_connection', { data: data || {} }),
  createPayByLink: (invoice_name: string, amount?: number) =>
    api.post('/method/saas_pos.api.teya.pay_by_link.create_invoice_payment_link', {
      invoice_name,
      amount,
    }),
};

export const posApi = {
  exportCatalogData: () => api.get('/method/saas_pos.api.data_import.api.download_full_export', { responseType: 'blob' }),
  exportCustomersData: () => api.get('/method/saas_pos.api.data_import.api.download_customers_export', { responseType: 'blob' }),
  exportSuppliersData: () => api.get('/method/saas_pos.api.data_import.api.download_suppliers_export', { responseType: 'blob' }),
  getItems: (params?: {
    search?: string;
    category?: string;
    item_type?: string;
    start?: number;
    limit?: number;
    include_inactive?: boolean | number;
    is_active?: boolean | number;
  }) =>
    api.get('/method/saas_pos.api.pos.get_items', {
      params: {
        ...params,
        include_inactive: params?.include_inactive ? 1 : undefined,
        is_active:
          params?.is_active === undefined || params?.is_active === null
            ? undefined
            : params.is_active
              ? 1
              : 0,
      },
    }),
  getStockReport: (params?: {
    category?: string;
    item?: string;
    from_date?: string;
    to_date?: string;
    by_batch?: number;
    expiring_within_days?: number;
  }) =>
    api.get('/method/saas_pos.api.reports.get_stock_report', { params }),
  getStockAlertsReport: (params?: {
    mode?: string;
    threshold?: number;
    category?: string;
    item?: string;
  }) =>
    api.get('/method/saas_pos.api.reports.get_stock_alerts_report', { params }),
  getStockValuationReport: (params?: {
    category?: string;
    item?: string;
    include_zero?: number;
  }) =>
    api.get('/method/saas_pos.api.reports.get_stock_valuation_report', { params }),
  getDamageLossReport: (params: {
    from_date: string;
    to_date: string;
    category?: string;
    item?: string;
  }) =>
    api.get('/method/saas_pos.api.reports.get_damage_loss_report', { params }),
  getSalaryReport: (params: { from_date: string; to_date: string }) =>
    api.get('/method/saas_pos.api.reports.get_salary_report', { params }),
  // Data Management
  clearSalesData: (password: string) =>
    api.post('/method/saas_pos.api.data_management.clear_sales_data', { password }),

  getCustomFields: (includeInactive = false) =>
    api.get('/method/saas_pos.api.pos.get_custom_fields', { params: { include_inactive: includeInactive ? 1 : 0 } }),
  createCustomField: (data: unknown) =>
    api.post('/method/saas_pos.api.pos.create_custom_field', { data }),
  updateCustomField: (data: unknown) =>
    api.post('/method/saas_pos.api.pos.update_custom_field', { data }),
  deleteCustomField: (name: string) =>
    api.post('/method/saas_pos.api.pos.delete_custom_field', { name }),
  getServicesWithVariants: () =>
    api.get('/method/saas_pos.api.pos.get_services_with_variants'),
  getCategories: (params?: { category_type?: string }) =>
    api.get('/method/saas_pos.api.pos.get_categories', { params }),
  createCategory: (data: unknown) =>
    api.post('/method/saas_pos.api.pos.create_category', { data }),
  updateCategory: (data: unknown) =>
    api.post('/method/saas_pos.api.pos.update_category', { data }),
  deleteCategory: (name: string) =>
    api.post('/method/saas_pos.api.pos.delete_category', { name }),
  getBrands: () =>
    api.get('/method/saas_pos.api.pos.get_brands'),
  createBrand: (data: unknown) =>
    api.post('/method/saas_pos.api.pos.create_brand', { data }),
  updateBrand: (data: unknown) =>
    api.post('/method/saas_pos.api.pos.update_brand', { data }),
  getModels: () =>
    api.get('/method/saas_pos.api.pos.get_models'),
  createModel: (data: unknown) =>
    api.post('/method/saas_pos.api.pos.create_model', { data }),
  updateModel: (data: unknown) =>
    api.post('/method/saas_pos.api.pos.update_model', { data }),
  getUOMs: () => api.get('/method/saas_pos.api.pos.get_uoms'),
  getPackageLimits: () => api.get('/method/saas_pos.api.pos.get_package_limits'),
  createStockIn: (data: unknown) =>
    api.post('/method/saas_pos.api.pos.create_stock_in', { data }),
  createStockOut: (data: unknown) =>
    api.post('/method/saas_pos.api.pos.create_stock_out', { data }),
  getItemStock: (item: string, variant?: string) =>
    api.get('/method/saas_pos.api.pos.get_item_stock', { params: { item, variant } }),
  getItemMinRate: (item: string, variant?: string) =>
    api.get('/method/saas_pos.api.pos.get_item_min_rate', { params: { item, variant } }),
  getCustomers: (params?: { search?: string; start?: number; limit?: number }) =>
    api.get('/method/saas_pos.api.pos.get_customers', { params }),
  getCustomerDashboard: (customer: string) =>
    api.get('/method/saas_pos.api.customer_dashboard.get_customer_dashboard', { params: { customer } }),
  getCustomerFulfillmentOrders: (params: {
    customer: string;
    fulfillment_type?: string;
    status?: string;
    page?: number;
    limit?: number;
  }) =>
    api.get('/method/saas_pos.api.deliveries.get_customer_fulfillment_orders', { params }),
  getCustomerLedger: (params: { from_date: string; to_date: string; customer?: string; group_by: string }) =>
    api.get('/method/saas_pos.api.pos.get_customer_ledger', { params }),
  getPartyLedger: (params: {
    party_type: 'POS Customer' | 'POS Supplier';
    party: string;
    from_date: string;
    to_date: string;
  }) => api.get('/method/saas_pos.api.party_ledger.get_party_ledger', { params }),
  getBankLedger: (params: {
    bank_account: string;
    from_date: string;
    to_date: string;
  }) => api.get('/method/saas_pos.api.bank_ledger.get_bank_ledger', { params }),
  getCashLedger: (params: {
    from_date: string;
    to_date: string;
  }) => api.get('/method/saas_pos.api.cash_ledger.get_cash_ledger', { params }),
  getCashSummary: (params: {
    from_date: string;
    to_date: string;
  }) => api.get('/method/saas_pos.api.cash_summary.get_cash_summary', { params }),
  getManagerCashReport: (params: {
    from_date: string;
    to_date: string;
    manager?: string;
  }) => api.get('/method/saas_pos.api.cash_summary.get_manager_cash_report', { params }),
  createCustomer: (data: unknown) =>
    api.post('/method/saas_pos.api.pos.create_customer', { data }),
  deleteCustomer: (name: string) =>
    api.post('/method/saas_pos.api.pos.delete_customer', { name }),
  downloadCustomerImportTemplate: () =>
    api.get('/method/saas_pos.api.data_import.api.download_template', {
      params: { entity: 'customer' },
      responseType: 'blob',
    }),
  importCustomers: (formData: FormData) =>
    api.post('/method/saas_pos.api.data_import.api.import_rows', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  getSuppliers: (params?: { search?: string; start?: number; limit?: number }) =>
    api.get('/method/saas_pos.api.pos.get_suppliers', { params }),
  getItemSuppliers: (params: { item: string; variant?: string; search?: string; limit?: number }) =>
    api.get('/method/saas_pos.api.pos.get_item_suppliers', { params }),
  createSupplier: (data: unknown) =>
    api.post('/method/saas_pos.api.pos.create_supplier', { data }),
  deleteSupplier: (name: string) =>
    api.post('/method/saas_pos.api.pos.delete_supplier', { name }),
  downloadSupplierImportTemplate: () =>
    api.get('/method/saas_pos.api.data_import.api.download_template', {
      params: { entity: 'supplier' },
      responseType: 'blob',
    }),
  importSuppliers: (formData: FormData) =>
    api.post('/method/saas_pos.api.data_import.api.import_rows', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  createPurchaseInvoice: (data: any) =>
    api.post('/method/saas_pos.api.purchase.create_purchase_invoice', { payload: data }),
  getPurchaseInvoices: (params: { supplier: string; from_date?: string; to_date?: string; status?: string }) =>
    api.get('/method/saas_pos.api.purchase.get_purchase_invoices', { params }),
  deletePurchaseInvoice: (name: string) =>
    api.post('/method/saas_pos.api.purchase.delete_purchase_invoice', { name }),
  addPurchaseInvoicePayment: (invoice_name: string, data: any) =>
    api.post('/method/saas_pos.api.purchase.add_payment', { invoice_name, payload: data }),
  addSupplierPayment: (data: any) =>
    api.post('/method/saas_pos.api.purchase.add_supplier_payment', { payload: data }),
  getSupplierDirectPayments: (supplier: string) =>
    api.get('/method/saas_pos.api.purchase.get_supplier_direct_payments', { params: { supplier } }),
  getSupplierAdvance: (supplier: string) =>
    api.get('/method/saas_pos.api.pos.get_supplier_advance', { params: { supplier } }),

  getCustomerInvoices: (params: { customer: string; from_date?: string; to_date?: string; status?: string }) =>
    api.get('/method/saas_pos.api.sales.get_customer_invoices', { params }),
  addSalePayment: (invoice_name: string, data: any) =>
    api.post('/method/saas_pos.api.sales.add_sale_payment', { invoice_name, payload: data }),
  addCustomerPayment: (data: any) =>
    api.post('/method/saas_pos.api.sales.add_customer_payment', { payload: data }),
  getCustomerDirectPayments: (customer: string) =>
    api.get('/method/saas_pos.api.pos.get_customer_direct_payments', { params: { customer } }),
  getReturnableInvoices: (timeFilter: string) =>
    api.get('/method/saas_pos.api.pos.get_returnable_invoices', { params: { time_filter: timeFilter } }),
  cancelPaymentEntry: (name: string, reason?: string) =>
    api.post('/method/saas_pos.api.pos.cancel_payment_entry', { name, reason }),
  getPendingInvoices: (params?: { from_date?: string; to_date?: string; invoice_no?: string }) =>
    api.get('/method/saas_pos.api.sales.get_pending_invoices', { params }),
  getCities: () =>
    api.get('/method/saas_pos.api.saas.get_cities'),
  createItem: (data: unknown) =>
    api.post('/method/saas_pos.api.pos.create_item', data),
  deleteItem: (name: string, check_only?: number) =>
    api.post('/method/saas_pos.api.pos.delete_item', { name, check_only }),
  createVariant: (data: {
    parent_item: string;
    variant_name: string;
    price: number;
    wholesale_price?: number;
    delivery_price?: number;
    reseller_price?: number;
    minimum_price?: number;
    retail_minimum_price?: number;
    cost_price?: number;
    unit_of_measurement?: string;
    barcode?: string;
  }) => api.post('/method/saas_pos.api.pos.create_variant', { data }),
  updateVariant: (data: {
    name: string;
    variant_name: string;
    price: number;
    wholesale_price?: number;
    delivery_price?: number;
    reseller_price?: number;
    minimum_price?: number;
    retail_minimum_price?: number;
    cost_price?: number;
    unit_of_measurement?: string;
    barcode?: string;
  }) => api.post('/method/saas_pos.api.pos.update_variant', { data }),
  deleteVariant: (name: string, check_only?: number) =>
    api.post('/method/saas_pos.api.pos.delete_variant', { name, check_only }),
  getItemVariants: (parent_item: string) =>
    api.get('/method/saas_pos.api.pos.get_item_variants', { params: { parent_item } }),
  bulkUpdateItemPrices: (items: Array<{
    name: string;
    variant?: string;
    sale_price?: number;
    wholesale_price?: number;
    reseller_price?: number;
    minimum_price?: number;
    retail_minimum_price?: number;
    cost_price?: number;
    delivery_price?: number;
  }>) => api.post('/method/saas_pos.api.pos.bulk_update_item_prices', { items }),
  applyPriceRule: (payload: {
    rule: { type: string; value?: number; round_to?: number; price_field?: string };
    filters?: { search?: string; categories?: string[]; item_type?: string; item_names?: string[] };
  }) => api.post('/method/saas_pos.api.pos.apply_price_rule', payload),
  lookupPublicBarcode: (barcode: string) =>
    api.get('/method/saas_pos.api.pos.lookup_public_barcode', { params: { barcode } }),
  createItemFromBarcodeScan: (data: {
    barcode: string;
    item_name: string;
    sale_price: number;
    brand?: string;
    size?: string;
    category?: string;
    tax_rate?: number;
    description?: string;
    image_url?: string;
    lookup_source?: string | null;
    manager_approver?: string;
  }) => api.post('/method/saas_pos.api.pos.create_item_from_barcode_scan', data),
  scanBarcode: (
    barcode: string,
    opts?: {
      enableScaleBarcode?: boolean;
      profile?: ScaleBarcodeProfile | Record<string, unknown>;
    },
  ) =>
    api.get('/method/saas_pos.api.pos.scan_barcode', {
      params: {
        barcode,
        enable_scale_barcode:
          opts?.enableScaleBarcode === undefined ? 1 : opts.enableScaleBarcode ? 1 : 0,
        scale_barcode_profile: opts?.profile ? JSON.stringify(opts.profile) : undefined,
      },
    }),
  getItemBatches: (item: string, variant?: string, include_zero?: number) =>
    api.get('/method/saas_pos.api.pos.get_item_batches', {
      params: { item, variant, include_zero },
    }),
  getItemSerials: (item: string, variant?: string, limit?: number) =>
    api.get('/method/saas_pos.api.pos.get_item_serials', {
      params: { item, variant, limit },
    }),
  getInvoiceForReturn: (invoice: string) =>
    api.get('/method/saas_pos.api.pos.get_invoice_for_return', { params: { invoice } }),
  getMyShiftReturnCount: (shift: string) =>
    api.get('/method/saas_pos.api.pos.get_my_shift_return_count', { params: { shift } }),
  createSalesReturn: (data: {
    pos_invoice: string;
    pos_shift?: string;
    items: { invoice_item: string; quantity: number }[];
    return_method?: string;
    return_bank?: string;
    manager_approver?: string;
  }) => api.post('/method/saas_pos.api.pos.create_sales_return', { data }),
  generateItemBarcode: (item: string) =>
    api.post('/method/saas_pos.api.pos.generate_item_barcode', { item }),
  generateVariantBarcode: (variant: string) =>
    api.post('/method/saas_pos.api.pos.generate_variant_barcode', { variant }),
  createInvoice: (data: unknown) =>
    api.post('/method/saas_pos.api.pos.create_invoice', { data }),
  getInvoiceDetails: (name: string) =>
    api.get('/method/saas_pos.api.pos.get_invoice_details', { params: { name } }),
  getDailySummary: () =>
    api.get('/method/saas_pos.api.pos.get_daily_summary'),
  getReturnedInvoices: (fromDate: string, toDate: string, groupBy: string) =>
    api.get('/method/saas_pos.api.reports.get_returned_invoices', { params: { from_date: fromDate, to_date: toDate, group_by: groupBy } }),
  getReportSummary: (fromDate: string, toDate: string, period?: string) =>
    api.get('/method/saas_pos.api.pos.get_report_summary', { params: { from_date: fromDate, to_date: toDate, period } }),
  getRecentInvoices: (params?: { limit?: number; start?: number; status?: string }) =>
    api.get('/method/saas_pos.api.pos.get_recent_invoices', { params }),
  getCashPockets: () =>
    api.get('/method/saas_pos.api.pos.get_cash_pockets'),
  createCashPocket: (data: { pocket_name: string; opening_balance: number; is_default: boolean }) =>
    api.post('/method/saas_pos.api.pos.create_cash_pocket', { data }),
  updateCashPocket: (data: { name: string; pocket_name: string; is_default: boolean }) =>
    api.post('/method/saas_pos.api.pos.update_cash_pocket', { data }),
  deleteCashPocket: (name: string) =>
    api.post('/method/saas_pos.api.pos.delete_cash_pocket', { name }),
  getBankAccounts: () =>
    api.get('/method/saas_pos.api.pos.get_bank_accounts'),
  createBankAccount: (data: {
    bank_name: string;
    account_title?: string;
    account_number?: string;
    opening_balance?: number;
    is_default?: boolean;
    logo?: string;
  }) => api.post('/method/saas_pos.api.pos.create_bank_account', { data }),
  updateBankAccount: (data: {
    name: string;
    bank_name: string;
    account_title?: string;
    account_number?: string;
    opening_balance?: number;
    is_default?: boolean;
    logo?: string;
  }) => api.post('/method/saas_pos.api.pos.update_bank_account', { data }),
  deleteBankAccount: (name: string) =>
    api.post('/method/saas_pos.api.pos.delete_bank_account', { name }),
  createBankTransfer: (data: {
    from_bank: string;
    to_bank: string;
    amount: number;
    remarks?: string;
  }) => api.post('/method/saas_pos.api.bank_transfer.create_bank_transfer', { data }),
  createCashDepositWithdraw: (data: {
    transaction_type: 'Deposit' | 'Withdraw';
    bank_name: string;
    amount: number;
    remarks?: string;
  }) => api.post('/method/saas_pos.api.bank_transfer.create_cash_deposit_withdraw', { data }),
  getPrinterSettings: () =>
    api.get('/method/saas_pos.api.pos.get_printer_settings'),
  updatePrinterSettings: (data: {
    printer_enabled?: number | boolean;
    printer_type?: string;
    printer_ip?: string;
    printer_port?: number;
    printer_name?: string;
    paper_width?: string;
    auto_print_receipt?: number | boolean;
  }) => api.post('/method/saas_pos.api.pos.update_printer_settings', data),
  getPosSettings: () =>
    api.get('/method/saas_pos.api.pos.get_pos_settings'),
  updatePosSettings: (data: {
    allow_sell_out_of_stock_item?: boolean;
    max_discount?: number;
    max_round_off?: number;
    show_tax_field_in_pos?: boolean;
    show_tax_in_invoice?: boolean;
    show_discount_field_in_pos?: boolean;
    show_discount_in_invoice_print?: boolean;
    print_receipt_after_sale?: boolean;
    show_today_invoices_to_cashier?: boolean;
    pos_screen_layout?: 'Detail Card View' | 'Image Grid View';
    pos_category_position?: 'Top' | 'Bottom' | 'Left' | 'Right';
    pos_sync_mode?: 'Live Online' | 'Local First Auto Sync' | 'Offline Always Manual Sync';
    offline_sync_allow_negative_stock?: boolean;
    offline_invoice_prefix?: string;
    offline_bypass_shift_in_local_modes?: boolean;
    use_barcode_or_qr?: 'Barcode' | 'QR Code';
    print_price_on_label?: boolean;
    open_drawer_on_cash_payment?: boolean;
    open_drawer_on_cash_movement?: boolean;
    customer_display_enabled?: boolean;
    blind_shift_close?: boolean;
    pos_touch_mode?: boolean;
    variance_approval_threshold?: number;
    enforce_shift_time_limit?: boolean;
    max_shift_hours?: number;
    shift_expiry_warning_hours?: number;
    enable_commission?: boolean;
  }) => api.post('/method/saas_pos.api.pos.update_pos_settings', { data }),

  getOfflineBootstrap: (params?: { limit_items?: number; limit_customers?: number; limit_suppliers?: number }) =>
    api.get('/method/saas_pos.api.offline_store.get_offline_bootstrap', { params }),
  getPosSyncStatus: () =>
    api.get('/method/saas_pos.api.offline_store.get_pos_sync_status'),
  getOfflineCatalogMeta: () =>
    api.get('/method/saas_pos.api.offline_store.get_offline_catalog_meta'),
  getOfflineCatalogChunk: (params: { entity: string; start?: number; limit?: number }) =>
    api.get('/method/saas_pos.api.offline_store.get_offline_catalog_chunk', { params }),

  getTodayCashierInvoices: (params?: { search?: string; duration?: string }) =>
    api.get('/method/saas_pos.api.pos.get_today_cashier_invoices', { params }),

  cancelPOSInvoice: (invoiceId: string) =>
    api.post('/method/saas_pos.api.pos.cancel_pos_invoice', { invoice_id: invoiceId }),

  createPOSExpense: (data: any) =>
    api.post('/method/saas_pos.api.expense.create_pos_expense', { data }),

  cancelPOSExpense: (data: { name: string; cancellation_reason: string }) =>
    api.post('/method/saas_pos.api.expense.cancel_pos_expense', { data }),

  deletePOSExpense: (data: { name: string }) =>
    api.post('/method/saas_pos.api.expense.delete_pos_expense', { data }),

  getPOSExpenses: (params?: { pos_shift?: string; posting_date?: string }) =>
    api.get('/method/saas_pos.api.expense.get_pos_expenses', { params }),

  getHotSellingItems: (params?: { limit?: number; days?: number; item_type?: string }) =>
    api.get('/method/saas_pos.api.pos.get_hot_selling_items', { params }),

  /** @deprecated use getHotSellingItems */
  getRecentItems: (params?: { limit?: number; days?: number }) =>
    api.get('/method/saas_pos.api.pos.get_hot_selling_items', { params }),

  getUserFavorites: () => api.get('/method/saas_pos.api.pos.get_user_favorites'),

  setUserFavorites: (items: string[]) =>
    api.post('/method/saas_pos.api.pos.set_user_favorites', { data: { items } }),

  getCustomerDisplaySnapshot: (companyCode?: string) =>
    api.get('/method/saas_pos.api.pos.get_customer_display_snapshot', {
      params: companyCode ? { company_code: companyCode } : undefined,
      ...(companyCode ? { headers: { 'X-Company-Code': companyCode } } : {}),
    }),

  pushCustomerDisplaySnapshot: (data: Record<string, unknown>) =>
    api.post('/method/saas_pos.api.pos.push_customer_display_snapshot', { data }),
    
  createQuotation: (data: Record<string, unknown>) =>
    api.post('/method/saas_pos.api.quotation.create_quotation', { data }),
  getQuotation: (id: string) =>
    api.get('/method/saas_pos.api.quotation.get_quotation_details', { params: { quotation_id: id } }),
  getQuotations: (params?: { customer?: string, search?: string, status?: string, page?: number, limit?: number }) =>
    api.get('/method/saas_pos.api.quotation.get_quotations', { params }),
  convertQuotation: (
    data:
      | string
      | {
          quotation_id: string;
          payment_method?: string;
          pos_shift?: string;
          payments?: Array<{ payment_method: string; amount: number; reference_no?: string }>;
        }
  ) => {
    const payload = typeof data === 'string' ? { quotation_id: data } : data;
    return api.post('/method/saas_pos.api.quotation.convert_to_sale', payload);
  },
  deleteQuotation: (quotation_id: string) =>
    api.post('/method/saas_pos.api.quotation.delete_quotation', { quotation_id }),
};

export const shiftApi = {
  getOpenShift: () => api.get('/method/saas_pos.api.shift.get_open_shift'),
  startShift: (data: { cash_pocket: string; opening_amount: number }) =>
    api.post('/method/saas_pos.api.shift.start_shift', { data }),
  getSummary: (shift: string) =>
    api.get('/method/saas_pos.api.shift.get_shift_summary', { params: { shift } }),
  closeShift: (data: {
    shift: string;
    closing_amount: number;
    cash_handover: number;
    remarks?: string;
    handover_mode: 'Cash' | 'Bank';
    handover_to?: string;
    handover_bank?: string;
  }) => api.post('/method/saas_pos.api.shift.close_shift', { data }),
  getCloseReport: (shift: string, detailType: 'invoice' | 'item') =>
    api.get('/method/saas_pos.api.shift.get_shift_close_report', {
      params: { shift, detail_type: detailType },
    }),
  getXReport: (params?: { shift?: string; detail_type?: 'invoice' | 'item' }) =>
    api.get('/method/saas_pos.api.shift.get_x_report', { params }),
  getZReport: (shift: string, detailType: 'invoice' | 'item' = 'invoice') =>
    api.get('/method/saas_pos.api.shift.get_z_report', {
      params: { shift, detail_type: detailType },
    }),
  getDayZReport: (date: string) =>
    api.get('/method/saas_pos.api.shift.get_day_z_report', { params: { date } }),
  listOpenShifts: () => api.get('/method/saas_pos.api.shift.list_open_shifts'),
  listClosedShiftsForDate: (date: string) =>
    api.get('/method/saas_pos.api.shift.list_closed_shifts_for_date', { params: { date } }),
  listShifts: (params?: { limit?: number; status?: string }) =>
    api.get('/method/saas_pos.api.shift.list_shifts', { params }),
  approveVariance: (data: { variance: string; approval_remarks?: string; linked_payments?: string[] }) =>
    api.post('/method/saas_pos.api.shift.approve_shift_variance', data),
  rejectVariance: (data: { variance: string; approval_remarks?: string }) =>
    api.post('/method/saas_pos.api.shift.reject_shift_variance', data),
  listPendingVariances: () => api.get('/method/saas_pos.api.shift.list_pending_variances'),
  getRecentSupplierPayments: () => api.get('/method/saas_pos.api.shift.get_recent_unlinked_supplier_payments'),
  getShiftPerformanceReport: (params: { from_date: string; to_date: string; cashier?: string }) =>
    api.get('/method/saas_pos.api.shift.get_shift_performance_report', { params }),
  getMyShifts: (params?: { limit?: number; status?: string }) => 
    api.get('/method/saas_pos.api.shift.get_my_shifts', { params }),
  resettleRejectedShift: (data: { shift: string; closing_amount: number; cash_handover: number; remarks?: string }) =>
    api.post('/method/saas_pos.api.shift.resettle_rejected_shift', data),
};

export const promotionsApi = {
  list: () => api.get('/method/saas_pos.api.promotions.list_promotion_campaigns'),
  get: (name: string) =>
    api.get('/method/saas_pos.api.promotions.get_promotion_campaign', { params: { name } }),
  getActive: () => api.get('/method/saas_pos.api.promotions.get_active_promotions_for_pos'),
  save: (data: Record<string, unknown>) =>
    api.post('/method/saas_pos.api.promotions.save_promotion_campaign', { data }),
  delete: (name: string) =>
    api.post('/method/saas_pos.api.promotions.delete_promotion_campaign', { name }),
  setActive: (name: string, is_active: number) =>
    api.post('/method/saas_pos.api.promotions.set_promotion_campaign_active', { name, is_active }),
  getReport: (params: { from_date: string; to_date: string }) =>
    api.get('/method/saas_pos.api.promotions.get_promotion_report', { params }),
};

export const commissionApi = {
  listRules: () => api.get('/method/saas_pos.api.commission.list_commission_rules'),
  saveRule: (data: Record<string, unknown>) =>
    api.post('/method/saas_pos.api.commission.save_commission_rule', { data }),
  deleteRule: (name: string) =>
    api.post('/method/saas_pos.api.commission.delete_commission_rule', { name }),
  getReport: (params: { from_date: string; to_date: string; earner?: string; status?: string }) =>
    api.get('/method/saas_pos.api.commission.get_commission_report', { params }),
  updateStatus: (names: string[], status: 'Approved' | 'Paid' | 'Accrued') =>
    api.post('/method/saas_pos.api.commission.update_commission_status', { names, status }),
  listSalesPeople: () => api.get('/method/saas_pos.api.commission.list_sales_people'),
};

export const serviceJobsApi = {
  listStaff: () => api.get('/method/saas_pos.api.service_jobs.list_service_staff'),
  listJobs: (params?: Record<string, unknown>) =>
    api.get('/method/saas_pos.api.service_jobs.list_service_jobs', { params }),
  updateStatus: (name: string, status: string, notes?: string) =>
    api.post('/method/saas_pos.api.service_jobs.update_service_job_status', { name, status, notes }),
  assign: (name: string, assigned_to: string) =>
    api.post('/method/saas_pos.api.service_jobs.assign_service_job', { name, assigned_to }),
  myCommission: (params?: { from_date?: string; to_date?: string }) =>
    api.get('/method/saas_pos.api.service_jobs.get_my_service_commission_summary', { params }),
};

export interface SignupPayload {
  company_name: string;
  owner_name: string;
  email: string;
  mobile: string;
  city: string;
  province?: string;
  postal_code?: string;
  business_type: string;
  subdomain: string;
  password: string;
  confirm_password: string;
  load_sample_data: boolean;
  package_code: string;
  sell_only_products?: boolean;
  country: string;
  currency: string;
  ui_language: string;
  timezone?: string;
  referral_code?: string;
  selected_add_ons?: string[];
  addon_codes?: string[];
  base_domain?: string;
}

export interface SignupRegion {
  country: string;
  currency: string;
  suggested_language: string;
  suggested_city?: string;
  timezone?: string;
  detected?: boolean;
  source?: string;
}

export const inquiryApi = {
  submitInquiry: (data: { full_name: string; email: string; phone: string; subject: string; message: string }) =>
    api.post('/method/saas_pos.api.website.submit_inquiry', { data }),
  detectSignupRegion: (data?: {
    client_country?: string;
    client_city?: string;
    client_ip?: string;
    timezone?: string;
    browser_language?: string;
  }) =>
    api.post<{ message: SignupRegion }>('/method/saas_pos.api.saas.detect_signup_region', { data: data || {} }),
  submitSignup: (data: SignupPayload) =>
    api.post('/method/saas_pos.api.saas.signup', { data }),
  checkCompanyCode: (code: string) =>
    api.get('/method/saas_pos.api.website.check_company_code', { params: { code } }),
  checkSubdomain: (subdomain: string) =>
    api.get('/method/saas_pos.api.website.check_subdomain', { params: { subdomain } }),
  getCities: () =>
    api.get('/method/saas_pos.api.saas.get_cities'),
  getPackages: () =>
    api.get('/method/saas_pos.api.website.get_packages'),
};

export const reportsApi = {
  getDailyCashLedger: (posting_date: string) =>
    api
      .get('/method/saas_pos.api.reports.get_daily_cash_ledger', { params: { posting_date } })
      .then((res) => res.data.message),
  getRawInvoices: (fromDate?: string, toDate?: string) =>
    api.get('/method/saas_pos.api.reports.get_raw_invoices', { params: { from_date: fromDate, to_date: toDate } }).then(res => res.data.message),
  getRawShifts: (fromDate?: string, toDate?: string) =>
    api.get('/method/saas_pos.api.reports.get_raw_shifts', { params: { from_date: fromDate, to_date: toDate } }).then(res => res.data.message),
  getPurchaseRegister: (params: {
    from_date: string;
    to_date: string;
    supplier?: string;
    status?: string;
    group_by: string;
  }) =>
    api
      .get('/method/saas_pos.api.reports.get_purchase_register', { params })
      .then((res) => res.data.message),

  getPurchaseRegisterPaginated: (params: {
    from_date: string;
    to_date: string;
    supplier?: string;
    status?: string;
    group_by: string;
    page?: number;
    page_size?: number;
    export?: number;
  }) =>
    api
      .get('/method/saas_pos.api.purchase_reports.register.get_purchase_register', { params })
      .then((res) => res.data.message),

  getPurchaseSummary: (params: Record<string, unknown>) =>
    api
      .get('/method/saas_pos.api.purchase_reports.summary.get_purchase_summary', { params })
      .then((res) => res.data.message),

  getSupplierPurchaseSummary: (params: Record<string, unknown>) =>
    api
      .get('/method/saas_pos.api.purchase_reports.supplier_summary.get_supplier_purchase_summary', { params })
      .then((res) => res.data.message),

  getPurchaseActivityInvoice: (params: Record<string, unknown>) =>
    api
      .get('/method/saas_pos.api.purchase_reports.activity_invoice.get_purchase_activity_invoice', { params })
      .then((res) => res.data.message),

  getPurchaseActivitySupplier: (params: Record<string, unknown>) =>
    api
      .get('/method/saas_pos.api.purchase_reports.activity_supplier.get_purchase_activity_supplier', { params })
      .then((res) => res.data.message),

  getItemPurchaseAnalysis: (params: Record<string, unknown>) =>
    api
      .get('/method/saas_pos.api.purchase_reports.item_analysis.get_item_purchase_analysis', { params })
      .then((res) => res.data.message),

  getPurchasePaymentReport: (params: Record<string, unknown>) =>
    api
      .get('/method/saas_pos.api.purchase_reports.payments.get_purchase_payment_report', { params })
      .then((res) => res.data.message),

  getOutstandingPayables: (params: Record<string, unknown>) =>
    api
      .get('/method/saas_pos.api.purchase_reports.outstanding.get_outstanding_payables', { params })
      .then((res) => res.data.message),

  getVatReturnSummary: (params: {
    from_date: string;
    to_date: string;
  }) =>
    api.get('/method/saas_pos.api.reports.get_vat_return_summary', { params }).then((res) => res.data.message),

  getSaleSummary: (params: {
    from_date: string;
    to_date: string;
    cashier?: string;
    customer?: string;
    payment_method?: string;
  }) =>
    api.get('/method/saas_pos.api.reports.get_sale_summary', { params }).then((res) => res.data.message),

  getPartySaleSummary: (params: {
    from_date: string;
    to_date: string;
    customer?: string;
    group_by: string;
  }) =>
    api.get('/method/saas_pos.api.reports.get_party_sale_summary', { params }).then((res) => res.data.message),

  getRecoveryReceipts: (params: {
    from_date: string;
    to_date: string;
    customer?: string;
    payment_method?: string;
    cashier?: string;
  }) =>
    api.get('/method/saas_pos.api.reports.get_recovery_receipts', { params }).then((res) => res.data.message),

  getSaleActivityInvoice: (params: { from_date: string; to_date: string; cashier?: string }) =>
    api.get('/method/saas_pos.api.reports.get_sale_activity_invoice', { params }).then((res) => res.data.message),

  getSaleActivityParty: (params: { from_date: string; to_date: string; customer?: string }) =>
    api.get('/method/saas_pos.api.reports.get_sale_activity_party', { params }).then((res) => res.data.message),

  getItemsSaleSummary: (params: {
    from_date: string;
    to_date: string;
    category?: string;
    item?: string;
  }) =>
    api.get('/method/saas_pos.api.reports.get_items_sale_summary', { params }).then((res) => res.data.message),

  getItemsSaleAnalysis: (params: {
    from_date: string;
    to_date: string;
    category?: string;
    item?: string;
  }) =>
    api.get('/method/saas_pos.api.reports.get_items_sale_analysis', { params }).then((res) => res.data.message),

  getServicesAnalysis: (params: { from_date: string; to_date: string; category?: string; service?: string }) =>
    api.get('/method/saas_pos.api.reports.get_services_analysis', { params }).then((res) => res.data.message),

  getReportCashiers: () =>
    api.get('/method/saas_pos.api.reports.get_report_cashiers').then((res) => res.data.message),

  getReportPaymentMethods: () =>
    api.get('/method/saas_pos.api.reports.get_report_payment_methods').then((res) => res.data.message),
    
  getProfitLossReport: (params: { from_date: string; to_date: string }) =>
    api.get('/method/saas_pos.api.reports.get_profit_loss_report', { params }).then((res) => res.data.message),

  getServiceJobsReport: (params: {
    from_date: string;
    to_date: string;
    assigned_to?: string;
    status?: string;
  }) =>
    api.get('/method/saas_pos.api.reports.get_service_jobs_report', { params }).then((res) => res.data.message),

  getShopStatusReport: (params: { from_date: string; to_date: string }) =>
    api.get('/method/saas_pos.api.reports.get_shop_status_report', { params }).then((res) => res.data.message),

  getCashFlowDashboard: (date: string) =>
    api.get('/method/saas_pos.api.reports.get_cash_flow_dashboard', { params: { date } }),
};

export const fbrApi = {
  testConnection: (data: {
    fbr_environment?: string;
    fbr_pos_id?: string;
    fbr_ntn?: string;
    fbr_strn?: string;
    fbr_bearer_token?: string;
  }) => api.post('/method/saas_pos.api.fbr.test_fbr_connection', data),
  retryPendingInvoices: () => api.post('/method/saas_pos.api.fbr.retry_pending_fbr_invoices', {}),
};

export const zatcaApi = {
  testConnection: () => api.post('/method/saas_pos.api.zatca.test_zatca_connection', {}),
  retryPendingInvoices: () => api.post('/method/saas_pos.api.zatca.retry_pending_zatca_invoices', {}),
  onboardEgs: (otp: string) => api.post('/method/saas_pos.api.zatca.onboard_zatca_egs', { otp }),
};

export const whatsappApi = {
  testConnection: () => api.post('/method/saas_pos.api.whatsapp.test_whatsapp_connection', {}),
  regenerateVerifyToken: () => api.post('/method/saas_pos.api.whatsapp.regenerate_verify_token', {}),
  syncCatalog: () => api.post('/method/saas_pos.api.whatsapp.sync_whatsapp_catalog', {}),
  getCatalogStatus: () => api.get('/method/saas_pos.api.whatsapp.get_whatsapp_catalog_status'),
  getOrders: (params?: { status?: string; start?: number; limit?: number }) =>
    api.get('/method/saas_pos.api.whatsapp.get_whatsapp_orders', { params }),
  getOrder: (name: string) =>
    api.get('/method/saas_pos.api.whatsapp.get_whatsapp_order', { params: { name } }),
  confirmOrder: (name: string) =>
    api.post('/method/saas_pos.api.whatsapp.confirm_whatsapp_order', { name }),
  rejectOrder: (name: string, reason?: string) =>
    api.post('/method/saas_pos.api.whatsapp.reject_whatsapp_order', { name, reason }),
};

export const ebayApi = {
  getStatus: () => api.get('/method/saas_pos.api.ebay.get_ebay_status'),
  testConnection: () => api.post('/method/saas_pos.api.ebay.test_ebay_connection', {}, { timeout: 60000 }),
  syncProducts: () => api.post('/method/saas_pos.api.ebay.sync_ebay_products', { background: 1 }, { timeout: 60000 }),
  syncInventory: () => api.post('/method/saas_pos.api.ebay.sync_ebay_inventory', {}, { timeout: 120000 }),
  fetchOrders: (limit = 50) =>
    api.post('/method/saas_pos.api.ebay.fetch_ebay_orders', { limit }, { timeout: 120000 }),
  getOauthUrl: () => api.get('/method/saas_pos.api.ebay.get_ebay_oauth_url'),
  disconnect: () => api.post('/method/saas_pos.api.ebay.disconnect_ebay'),
  confirmOrder: (name: string) => api.post('/method/saas_pos.api.ebay.confirm_ebay_channel_order', { name }),
  rejectOrder: (name: string, reason?: string) =>
    api.post('/method/saas_pos.api.ebay.reject_ebay_channel_order', { name, reason }),
  getOrder: (name: string) =>
    api.get('/method/saas_pos.api.channels.get_channel_order', { params: { name } }),
  listChannelOrders: (params?: { status?: string; limit?: number }) =>
    api.get('/method/saas_pos.api.channels.list_channel_orders', {
      params: { channel: 'ebay', ...params },
    }),
};

export const amazonApi = {
  getStatus: () => api.get('/method/saas_pos.api.amazon.get_amazon_status'),
  testConnection: () =>
    api.post('/method/saas_pos.api.amazon.test_amazon_connection', {}, { timeout: 60000 }),
  disconnect: () => api.post('/method/saas_pos.api.amazon.disconnect_amazon'),
};

export const metaShopsApi = {
  getStatus: () => api.get('/method/saas_pos.api.meta_shops.get_meta_status'),
  testConnection: () =>
    api.post('/method/saas_pos.api.meta_shops.test_meta_connection', {}, { timeout: 60000 }),
  disconnect: () => api.post('/method/saas_pos.api.meta_shops.disconnect_meta'),
};

export const tiktokShopApi = {
  getStatus: () => api.get('/method/saas_pos.api.tiktok_shop.get_tiktok_status'),
  testConnection: () =>
    api.post('/method/saas_pos.api.tiktok_shop.test_tiktok_connection', {}, { timeout: 60000 }),
  disconnect: () => api.post('/method/saas_pos.api.tiktok_shop.disconnect_tiktok'),
};

export const onbuyApi = {
  getStatus: () => api.get('/method/saas_pos.api.onbuy.get_onbuy_status'),
  testConnection: () =>
    api.post('/method/saas_pos.api.onbuy.test_onbuy_connection', {}, { timeout: 60000 }),
  disconnect: () => api.post('/method/saas_pos.api.onbuy.disconnect_onbuy'),
};

export const channelPricingApi = {
  listChannels: () => api.get('/method/saas_pos.api.channels.list_channel_pricing_channels'),
  listCatalogue: (params: {
    channel: string;
    search?: string;
    category?: string;
    published_only?: number;
    limit?: number;
    offset?: number;
  }) => api.get('/method/saas_pos.api.channels.list_channel_catalogue', { params }),
  setPrice: (data: Record<string, unknown>) =>
    api.post('/method/saas_pos.api.channels.set_channel_item_price', { data }),
  setPrices: (data: {
    channel: string;
    items: Array<{
      item: string;
      channel_price?: number;
      clear_price?: number;
      channel_delivery_price?: number;
      clear_delivery?: number;
    }>;
    push?: number;
  }) => api.post('/method/saas_pos.api.channels.set_channel_item_prices', { data }),
};

/** Marketing Platform — WhatsApp / SMS / Email add-ons (not WhatsApp Shop). */
export const marketingApi = {
  listChannels: () => api.get('/method/saas_pos.api.marketing.channels.list_marketing_channels'),
  saveChannel: (data: Record<string, unknown>) =>
    api.post('/method/saas_pos.api.marketing.channels.save_marketing_channel', { data }),
  testChannel: (data: { name: string; to?: string }) =>
    api.post('/method/saas_pos.api.marketing.channels.test_marketing_channel', data),
  deleteChannel: (name: string) =>
    api.post('/method/saas_pos.api.marketing.channels.delete_marketing_channel', { name }),
  getNotificationSettings: () =>
    api.get('/method/saas_pos.api.marketing.notifications_api.get_marketing_notification_settings'),
  getUsageReport: (params?: {
    period?: string;
    from_date?: string;
    to_date?: string;
    limit?: number;
  }) =>
    api.get('/method/saas_pos.api.marketing.usage.get_marketing_usage_report', { params }),
  saveNotificationSettings: (data: Record<string, unknown>) =>
    api.post('/method/saas_pos.api.marketing.notifications_api.save_marketing_notification_settings', {
      data,
    }),
  previewNotificationTemplate: (data: { template?: string; event_key?: string }) =>
    api.post('/method/saas_pos.api.marketing.notifications_api.preview_marketing_notification_template', data),
  previewAudience: (data: Record<string, unknown>) =>
    api.post('/method/saas_pos.api.marketing.campaigns.preview_marketing_audience', data),
  listCampaigns: (limit = 50) =>
    api.get('/method/saas_pos.api.marketing.campaigns.list_marketing_campaigns', { params: { limit } }),
  getCampaign: (name: string) =>
    api.get('/method/saas_pos.api.marketing.campaigns.get_marketing_campaign', { params: { name } }),
  saveCampaign: (data: Record<string, unknown>) =>
    api.post('/method/saas_pos.api.marketing.campaigns.save_marketing_campaign', { data }),
  sendCampaign: (name: string) =>
    api.post('/method/saas_pos.api.marketing.campaigns.send_marketing_campaign', { name }),
  cancelCampaign: (name: string) =>
    api.post('/method/saas_pos.api.marketing.campaigns.cancel_marketing_campaign', { name }),
};

/** Channel Manager — list/status for Marketplace hub (no secrets). */
export const channelsApi = {
  listChannels: () => api.get('/method/saas_pos.api.channels.list_channels'),
  getStatus: (channel: string) =>
    api.get('/method/saas_pos.api.channels.get_channel_status', { params: { channel } }),
  testConnection: (channel: string) =>
    api.post('/method/saas_pos.api.channels.test_channel_connection', { channel }),
  getConnectHelp: (channel: string) =>
    api.get('/method/saas_pos.api.channels.get_channel_connect_help', { params: { channel } }),
  listOrders: (params?: { channel?: string; status?: string; limit?: number }) =>
    api.get('/method/saas_pos.api.channels.list_channel_orders', { params }),
  rejectOrder: (name: string, reason?: string) =>
    api.post('/method/saas_pos.api.channels.reject_channel_order', { name, reason }),
};

const shopifyLongTimeout = { timeout: 180000 };

export const shopifyApi = {
  testConnection: () =>
    api.post('/method/saas_pos.api.shopify.test_shopify_connection', {}, shopifyLongTimeout),
  getStatus: () => api.get('/method/saas_pos.api.shopify.get_shopify_status'),
  syncProducts: () =>
    api.post('/method/saas_pos.api.shopify.sync_shopify_products', { background: 1 }, shopifyLongTimeout),
  syncInventory: () =>
    api.post('/method/saas_pos.api.shopify.sync_shopify_inventory', {}, shopifyLongTimeout),
  fetchOrders: (limit = 25) =>
    api.post('/method/saas_pos.api.shopify.fetch_shopify_orders', { limit }, shopifyLongTimeout),
  getOrders: (params?: { status?: string; start?: number; limit?: number }) =>
    api.get('/method/saas_pos.api.shopify.list_shopify_orders', { params }),
  getOrder: (name: string) =>
    api.get('/method/saas_pos.api.shopify.get_shopify_order', { params: { name } }),
  confirmOrder: (
    name: string,
    line_resolutions?: Array<{
      idx?: number;
      sku?: string;
      shopify_line_item_id?: string;
      action: 'create_product' | 'general_item';
    }>,
  ) =>
    api.post('/method/saas_pos.api.shopify.confirm_shopify_order', {
      name,
      line_resolutions: line_resolutions ? JSON.stringify(line_resolutions) : undefined,
    }),
  rejectOrder: (name: string) =>
    api.post('/method/saas_pos.api.shopify.reject_shopify_order', { name }),
  seedDemo: () => api.post('/method/saas_pos.api.shopify.seed_shopify_demo_data', {}, shopifyLongTimeout),
  importPreviewCollections: () =>
    api.get('/method/saas_pos.api.shopify.shopify_import_preview_collections'),
  importPreviewProducts: (params?: {
    cursor?: string;
    page_size?: number;
    search?: string;
    collection_id?: string;
    not_in_pos?: number;
  }) =>
    api.get('/method/saas_pos.api.shopify.shopify_import_preview_products', { params }),
  importApply: (payload: {
    rows: unknown[];
    import_categories?: number;
    import_stock?: number;
    skip_existing?: number;
    background?: number;
  }) =>
    api.post(
      '/method/saas_pos.api.shopify.shopify_import_apply',
      {
        rows: JSON.stringify(payload.rows || []),
        import_categories: payload.import_categories ?? 1,
        import_stock: payload.import_stock ?? 1,
        skip_existing: payload.skip_existing ?? 1,
        background: payload.background ?? 0,
      },
      shopifyLongTimeout,
    ),
  importJobStatus: () =>
    api.get('/method/saas_pos.api.shopify.shopify_import_job_status'),
  oauthStatus: () =>
    api.get('/method/saas_pos.api.shopify.shopify_oauth_status'),
  getOauthUrl: (shop?: string) =>
    api.post('/method/saas_pos.api.shopify.get_shopify_oauth_url', { shop }),
  disconnectOauth: () =>
    api.post('/method/saas_pos.api.shopify.disconnect_shopify_oauth'),
};

export const uaeApi = {
  exportInvoiceForAsp: (invoice_name: string) =>
    api.post('/method/saas_pos.api.uae.export_invoice_for_asp', { invoice_name }),
};

export const salesHubApi = {
  getCounts: () =>
    api.get('/method/saas_pos.api.sales_hub.get_sales_hub_counts'),
  getList: (params: {
    tab: 'quotes' | 'sales' | 'returns';
    search?: string;
    status?: string;
    customer?: string;
    sales_person?: string;
    from_date?: string;
    to_date?: string;
    page?: number;
    limit?: number;
    hide_walk_in?: boolean;
  }) =>
    api.get('/method/saas_pos.api.sales_hub.get_sales_hub_list', { params }),
  getDetail: (tab: 'quotes' | 'sales' | 'returns', doc_name: string) =>
    api.get('/method/saas_pos.api.sales_hub.get_sales_hub_detail', { params: { tab, doc_name } }),
  duplicateQuote: (quotation_id: string) =>
    api.post('/method/saas_pos.api.sales_hub.duplicate_quotation', { quotation_id }),
  deleteQuote: (quotation_id: string) =>
    api.post('/method/saas_pos.api.quotation.delete_quotation', { quotation_id }),
  convertQuote: (quotation_id: string) =>
    api.post('/method/saas_pos.api.quotation.convert_to_sale', { quotation_id }),
  createRecord: (data: Record<string, unknown>) =>
    api.post('/method/saas_pos.api.sales_hub.create_sales_hub_record', { data }),
  deleteSaleInvoice: (invoice_name: string, password?: string) =>
    api.post('/method/saas_pos.api.sales.delete_sale_invoice', { invoice_name, password }),
  clearSalesData: (password: string) =>
    api.post('/method/saas_pos.api.data_management.clear_sales_data', { password }),
};

export const deliveriesApi = {
  getCounts: (driver?: string) =>
    api.get('/method/saas_pos.api.deliveries.get_delivery_counts', { params: driver ? { driver } : undefined }),
  getList: (params?: {
    status?: string;
    search?: string;
    from_date?: string;
    to_date?: string;
    page?: number;
    limit?: number;
    customer?: string;
    driver?: string;
  }) =>
    api.get('/method/saas_pos.api.deliveries.get_deliveries', { params }),
  updateStatus: (invoice_name: string, status: string, notes?: string, delivery_driver?: string) =>
    api.post('/method/saas_pos.api.deliveries.update_delivery_status', {
      invoice_name,
      status,
      notes,
      delivery_driver,
    }),
  getTeamMembers: () =>
    api.get('/method/saas_pos.api.deliveries.get_delivery_team_members'),
  updateDriverLocation: (latitude: number, longitude: number, accuracy?: number) =>
    api.post('/method/saas_pos.api.deliveries.update_driver_location', {
      latitude,
      longitude,
      accuracy,
    }),
  getDriversLocations: (params?: { from_date?: string; to_date?: string }) =>
    api.get('/method/saas_pos.api.deliveries.get_delivery_drivers_locations', { params }),
  updateDeliveryCoordinates: (invoice_name: string, latitude: number, longitude: number) =>
    api.post('/method/saas_pos.api.deliveries.update_delivery_coordinates', {
      invoice_name,
      latitude,
      longitude,
    }),
};

export const storefrontAdminApi = {
  getSettings: () => api.get('/method/saas_pos.api.storefront.admin.get_storefront_admin_settings'),
  saveSettings: (data: Record<string, unknown>) =>
    api.post('/method/saas_pos.api.storefront.admin.save_storefront_admin_settings', { data }),
  verifyDomain: () => api.post('/method/saas_pos.api.storefront.admin.verify_custom_domain'),
  listCatalogue: (params?: {
    category?: string;
    search?: string;
    limit?: number;
    offset?: number;
    published_categories_only?: number;
  }) => api.get('/method/saas_pos.api.storefront.admin.list_catalogue_admin', { params }),
  setCategory: (data: Record<string, unknown>) =>
    api.post('/method/saas_pos.api.storefront.admin.set_category_storefront', data),
  setItem: (data: Record<string, unknown>) =>
    api.post('/method/saas_pos.api.storefront.admin.set_item_storefront', data),
  bulkPublishCategory: (category: string, publish = 1) =>
    api.post('/method/saas_pos.api.storefront.admin.bulk_publish_category', { category, publish }),
  reorderCategories: (order: string[]) =>
    api.post('/method/saas_pos.api.storefront.admin.reorder_storefront_categories', { order }),
  listZones: () => api.get('/method/saas_pos.api.storefront.admin.list_delivery_zones'),
  saveZone: (data: Record<string, unknown>) =>
    api.post('/method/saas_pos.api.storefront.admin.save_delivery_zone', { data }),
  deleteZone: (name: string) =>
    api.post('/method/saas_pos.api.storefront.admin.delete_delivery_zone', { name }),
  salesSummary: () => api.get('/method/saas_pos.api.storefront.admin.storefront_sales_summary'),
  listPages: () => api.get('/method/saas_pos.api.storefront.admin.list_storefront_pages'),
  getPage: (name: string) =>
    api.get('/method/saas_pos.api.storefront.admin.get_storefront_page', { params: { name } }),
  savePage: (data: Record<string, unknown>) =>
    api.post('/method/saas_pos.api.storefront.admin.save_storefront_page', { data }),
  deletePage: (name: string) =>
    api.post('/method/saas_pos.api.storefront.admin.delete_storefront_page', { name }),
  reorderPages: (order: string[]) =>
    api.post('/method/saas_pos.api.storefront.admin.reorder_storefront_pages', { order }),
  listStoreCustomers: (params?: { status?: string; search?: string; limit?: number }) =>
    api.get('/method/saas_pos.api.storefront.admin.list_store_customers', { params }),
  setStoreCustomerApproval: (name: string, status: string, reason?: string) =>
    api.post('/method/saas_pos.api.storefront.admin.set_store_customer_approval', {
      name,
      status,
      reason,
    }),
  getPosCustomerStoreLogin: (pos_customer: string) =>
    api.get('/method/saas_pos.api.storefront.admin.get_pos_customer_store_login', {
      params: { pos_customer },
    }),
  setPosCustomerStoreLogin: (data: Record<string, unknown>) =>
    api.post('/method/saas_pos.api.storefront.admin.set_pos_customer_store_login', { data }),
  disableStoreCustomerLogin: (opts: { name?: string; pos_customer?: string }) =>
    api.post('/method/saas_pos.api.storefront.admin.disable_store_customer_login', opts),
};

export const storefrontOrdersApi = {
  list: (params?: { status?: string; limit?: number; offset?: number }) =>
    api.get('/method/saas_pos.api.storefront.orders.list_storefront_orders', { params }),
  get: (name: string) =>
    api.get('/method/saas_pos.api.storefront.orders.get_storefront_order', { params: { name } }),
  accept: (name: string) =>
    api.post('/method/saas_pos.api.storefront.orders.accept_storefront_order', { name }),
  reject: (name: string, reason?: string) =>
    api.post('/method/saas_pos.api.storefront.orders.reject_storefront_order', { name, reason }),
};

export const stripeApi = {
  // Platform SaaS (Subscription / Support Page)
  getSaasConfig: () =>
    api.get('/method/saas_pos.api.stripe_api.get_saas_stripe_config'),
  createSubscriptionCheckout: (data: {
    checkout_type?: 'redirect' | 'embedded';
    invoice_names?: string[];
    package?: string;
    billing_cycle?: string;
    amount?: number;
    addon_codes?: string[];
    selected_add_ons?: string[];
  }) =>
    api.post('/method/saas_pos.api.stripe_api.create_subscription_checkout', { data }),
  verifySubscriptionPayment: (data: {
    session_id?: string;
    payment_intent_id?: string;
  }) =>
    api.post('/method/saas_pos.api.stripe_api.verify_subscription_payment', { data }),

  // Tenant Shop (Settings & Store Checkout)
  getTenantSettings: () =>
    api.get('/method/saas_pos.api.stripe_api.get_tenant_stripe_settings'),
  saveTenantSettings: (data: {
    enabled: boolean | number;
    mode: 'Sandbox' | 'Live';
    checkout_type: 'redirect' | 'embedded';
    currency: string;
    statement_descriptor?: string;
    auto_complete_order?: boolean | number;
    test_publishable_key?: string;
    test_secret_key?: string;
    test_webhook_secret?: string;
    live_publishable_key?: string;
    live_secret_key?: string;
    live_webhook_secret?: string;
  }) =>
    api.post('/method/saas_pos.api.stripe_api.save_tenant_stripe_settings', { data }),
  testTenantConnection: (data?: { mode?: 'Sandbox' | 'Live'; secret_key?: string }) =>
    api.post('/method/saas_pos.api.stripe_api.test_tenant_stripe_connection', { data }),
  createShopCheckout: (data: {
    amount: number;
    order_id?: string;
    invoice_id?: string;
    checkout_type?: 'redirect' | 'embedded';
  }) =>
    api.post('/method/saas_pos.api.stripe_api.create_shop_checkout', { data }),
};

export default api;